import React from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Spin,
  Statistic,
  Table,
  Typography,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { useParams } from "react-router-dom";
import useStudentPaymentStatus, {
  EnrichedStudentSchedule,
} from "../../hooks/useStudentPaymentStatus";
import { formatNumberPrice } from "../../../../shared/utils/Helper";
import {
  GlobalPaymentStatusBadge,
  PaymentStatusBadge,
  AcademyStatusBadge,
} from "../../../../shared/components/ui/AcademyStatusBadge";
import type { MonthPaymentValidity } from "../../utils/paymentQuoteUtils";

const { Text } = Typography;

const MONTH_VALIDITY_COLORS: Record<MonthPaymentValidity, string> = {
  vigente: "#4bd542",
  no_vigente: "#ff4d4f",
  sin_cuota: "blue",
};

interface StudentPaymentStatusTabProps {
  onPayClick?: () => void;
  readOnly?: boolean;
  refreshKey?: number;
}

const renderPeriod = (item: EnrichedStudentSchedule) =>
  item.month
    ? moment(item.month).format("MMM / YYYY")
    : item.payment_schedule?.month
      ? moment(item.payment_schedule.month).format("MMM / YYYY")
      : "—";

const baseColumns = [
  {
    title: "Periodo",
    key: "month",
    render: (_: unknown, item: EnrichedStudentSchedule) => renderPeriod(item),
  },
  {
    title: "Fecha límite",
    key: "payment_due_date",
    render: (_: unknown, item: EnrichedStudentSchedule) =>
      item.payment_due_date
        ? moment(item.payment_due_date).format("DD/MM/YYYY")
        : "—",
  },
  {
    title: "Valor extraordinario",
    key: "program_payment_schedule_price",
    render: (_: unknown, item: EnrichedStudentSchedule) =>
      formatNumberPrice(+item.program_payment_schedule_price),
  },
  {
    title: "Valor pronto pago",
    key: "program_payment_schedule_prompt_price",
    render: (_: unknown, item: EnrichedStudentSchedule) =>
      item.promptPrice > 0
        ? formatNumberPrice(+item.promptPrice)
        : "—",
  },
  {
    title: "Descuento pronto pago",
    key: "cash_discount",
    render: (_: unknown, item: EnrichedStudentSchedule) =>
      item.cashDiscountAmount > 0 ? (
        <Text style={{ color: "#4bd542" }}>
          -{formatNumberPrice(item.cashDiscountAmount)}
        </Text>
      ) : item.isPromptPaymentEligible ? (
        <Text type="secondary">Aplica antes del vencimiento</Text>
      ) : (
        "—"
      ),
  },
  {
    title: "Saldo a pagar",
    key: "effectiveBalance",
    render: (_: unknown, item: EnrichedStudentSchedule) => {
      const showPrompt =
        item.isPromptPaymentEligible &&
        item.promptPrice > 0 &&
        item.effectiveBalance < item.program_payment_schedule_price;

      return (
        <div>
          {showPrompt && (
            <Text delete type="secondary" style={{ display: "block" }}>
              {formatNumberPrice(+item.program_payment_schedule_price)}
            </Text>
          )}
          <Text strong={showPrompt}>
            {formatNumberPrice(item.effectiveBalance)}
          </Text>
        </div>
      );
    },
  },
  {
    title: "Estado",
    key: "payment_status",
    render: (_: unknown, item: EnrichedStudentSchedule) => (
      <PaymentStatusBadge status={item.payment_status} />
    ),
  },
];

export const StudentPaymentStatusTab = ({
  onPayClick,
  readOnly = false,
  refreshKey = 0,
}: StudentPaymentStatusTabProps) => {
  const { user_id, program_id } = useParams();
  const {
    loading,
    schedules,
    summary,
    upcomingSchedules,
    overdueSchedules,
    currentMonthSchedule,
    currentMonthValidity,
    refresh,
  } = useStudentPaymentStatus(+program_id!, +user_id!, refreshKey);

  const scheduleColumns = [
    ...baseColumns,
    {
      title: "Pagado",
      key: "amount_paid",
      render: (_: unknown, item: EnrichedStudentSchedule) =>
        formatNumberPrice(+item.amount_paid),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const hasPromptDiscount =
    summary.totalCourseValuePrompt < summary.totalCourseValue;

  const currentMonthLabel = currentMonthSchedule
    ? renderPeriod(currentMonthSchedule)
    : moment().format("MMM / YYYY");

  return (
    <div>
      <div className="flex justify-end gap-2 mb-4">
        <Button icon={<ReloadOutlined />} onClick={() => refresh()}>
          Actualizar
        </Button>
        {!readOnly && onPayClick && (
          <Button
            type="primary"
            shape="round"
            icon={<PlusOutlined />}
            size="large"
            onClick={onPayClick}
          >
            Pagar programa
          </Button>
        )}
      </div>

      <Card
        size="small"
        style={{
          marginBottom: 24,
          borderColor:
            MONTH_VALIDITY_COLORS[currentMonthValidity.validity] ?? undefined,
        }}
        title={`Pago del mes (${currentMonthLabel})`}
      >
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <AcademyStatusBadge
              background={
                MONTH_VALIDITY_COLORS[currentMonthValidity.validity]
              }
              label={currentMonthValidity.label}
            />
            {currentMonthSchedule && (
              <PaymentStatusBadge status={currentMonthSchedule.payment_status} />
            )}
          </div>
          {currentMonthSchedule ? (
            <div className="flex flex-wrap gap-6">
              <div>
                <Text type="secondary">Fecha límite</Text>
                <div>
                  {currentMonthSchedule.payment_due_date
                    ? moment(currentMonthSchedule.payment_due_date).format(
                        "DD/MM/YYYY"
                      )
                    : "—"}
                </div>
              </div>
              <div>
                <Text type="secondary">Saldo del mes</Text>
                <div>
                  {formatNumberPrice(currentMonthSchedule.effectiveBalance)}
                </div>
              </div>
              <div>
                <Text type="secondary">Pagado</Text>
                <div>
                  {formatNumberPrice(+currentMonthSchedule.amount_paid)}
                </div>
              </div>
            </div>
          ) : (
            <Text type="secondary">
              No hay una cuota asignada para el mes actual.
            </Text>
          )}
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Valor total del curso"
              value={summary.totalCourseValue}
              formatter={(v) => formatNumberPrice(Number(v))}
            />
            {hasPromptDiscount && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Pronto pago:{" "}
                {formatNumberPrice(summary.totalCourseValuePrompt)}
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total pagado"
              value={summary.totalPaid}
              formatter={(v) => formatNumberPrice(Number(v))}
              valueStyle={{ color: "#4bd542" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Debe actualmente"
              value={summary.currentBalance}
              formatter={(v) => formatNumberPrice(Number(v))}
              valueStyle={{
                color: summary.currentBalance > 0 ? "#ff4d4f" : "#4bd542",
              }}
            />
            {summary.promptPaymentDiscount > 0 && (
              <Text style={{ fontSize: 12, color: "#4bd542" }}>
                Incluye descuento pronto pago:{" "}
                {formatNumberPrice(summary.promptPaymentDiscount)}
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Ahorro pronto pago"
              value={summary.totalCourseValue - summary.totalCourseValuePrompt}
              formatter={(v) =>
                Number(v) > 0 ? formatNumberPrice(Number(v)) : "—"
              }
              valueStyle={{
                color:
                  summary.totalCourseValue - summary.totalCourseValuePrompt > 0
                    ? "#4bd542"
                    : undefined,
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card size="small">
            <Text type="secondary">Estado general</Text>
            <div style={{ marginTop: 8 }}>
              <GlobalPaymentStatusBadge
                status={summary.globalStatus}
                label={summary.globalStatusLabel}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {overdueSchedules.length > 0 && (
        <Card
          size="small"
          style={{ marginBottom: 16, borderColor: "#ff4d4f" }}
          title={
            <Text style={{ color: "#ff4d4f" }}>
              Cuotas en mora ({overdueSchedules.length})
            </Text>
          }
        >
          <Table
            size="small"
            pagination={false}
            dataSource={overdueSchedules}
            rowKey="id"
            columns={baseColumns}
            scroll={{ x: true }}
          />
        </Card>
      )}

      <Card size="small" title="Próximas cuotas" style={{ marginBottom: 16 }}>
        {upcomingSchedules.length > 0 ? (
          <Table
            size="small"
            pagination={false}
            dataSource={upcomingSchedules}
            rowKey="id"
            columns={baseColumns}
            scroll={{ x: true }}
          />
        ) : (
          <Text type="secondary">No hay cuotas pendientes por vencer.</Text>
        )}
      </Card>

      <Card size="small" title="Detalle de cuotas" style={{ marginBottom: 16 }}>
        {schedules.length > 0 ? (
          <Table
            size="small"
            dataSource={schedules}
            rowKey="id"
            columns={scheduleColumns}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
          />
        ) : (
          <Text type="secondary">
            {readOnly
              ? "Este estudiante no tiene cuotas asignadas para este programa."
              : 'Este estudiante no tiene cuotas asignadas. Usa la pestaña "Gestionar cronograma de cuotas" para asignarlas.'}
          </Text>
        )}
      </Card>
    </div>
  );
};
